import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  RefreshCw, AlertTriangle, TrendingUp, TrendingDown, Minus, Wallet, Receipt, Database, Ban, Gift, Activity, ChevronRight
} from 'lucide-react';
import { puntovService } from '../../../services/api';
import { useAuth } from '../../../hooks/useAuth';
import { fmtUsd, fmtPct, fmtEntero, fmtFecha, fmtPeriodo, periodoActual } from './formato';
import { useUmbralAlerta } from './ventasComun';

// Resumen para dirección: lo primero que mira el CEO. Todo lo calcula el
// backend (/puntov/resumen-direccion); aquí solo se presenta.

const ETIQUETA_TIPO = {
  zona_propia: { texto: 'Zona Punto V', clase: 'bg-blue-50 text-blue-700' },
  cliente: { texto: 'Cliente', clase: 'bg-gray-100 text-gray-700' }
};

const Variacion = ({ pct, grande = false }) => {
  if (pct === null || pct === undefined) return <span className="text-gray-400">sin comparación</span>;
  const Icono = pct > 0 ? TrendingUp : pct < 0 ? TrendingDown : Minus;
  const color = pct > 0 ? 'text-green-700' : pct < 0 ? 'text-red-700' : 'text-gray-500';
  return (
    <span className={`inline-flex items-center gap-1 tabular-nums font-medium ${color}`}>
      <Icono size={grande ? 14 : 12} />
      {pct > 0 ? '+' : ''}{fmtPct(pct)}
    </span>
  );
};

const Tarjeta = ({ titulo, valor, children, alerta = false }) => (
  <div className={`bg-white rounded-xl border p-4 min-w-0 ${alerta ? 'border-red-200' : 'border-gray-200'}`}>
    <div className="text-sm text-gray-500">{titulo}</div>
    <div className={`mt-1 text-2xl font-bold tabular-nums ${alerta ? 'text-red-700' : 'text-gray-900'}`}>{valor}</div>
    <div className="mt-1 text-xs text-gray-500 space-y-0.5">{children}</div>
  </div>
);

const ErrorSeccion = ({ mensaje }) => (mensaje ? (
  <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-3 text-xs flex items-start gap-2">
    <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" /> <span>No se pudo cargar esta sección: {mensaje}</span>
  </div>
) : null);

// Barra apilada de estados de máquina (mismos colores que el Monitor).
const BarraMaquinas = ({ m }) => {
  const partes = [
    { n: m.vendiendo, color: 'bg-green-500', texto: 'vendiendo' },
    { n: m.sin_vender, color: 'bg-amber-500', texto: 'sin vender' },
    { n: m.fuera_de_servicio, color: 'bg-gray-400', texto: 'fuera de servicio' },
    { n: m.inactivas, color: 'bg-gray-200', texto: 'inactivas' }
  ];
  if (!m.total) return <span className="text-xs text-gray-400">Sin máquinas</span>;
  return (
    <div className="min-w-[7rem]" title={partes.filter(p => p.n).map(p => `${p.n} ${p.texto}`).join(' · ')}>
      <div className="flex h-2 rounded overflow-hidden bg-gray-100">
        {partes.map(p => p.n > 0 && <div key={p.texto} className={p.color} style={{ width: `${(p.n / m.total) * 100}%` }} />)}
      </div>
      <div className="mt-1 text-[11px] text-gray-500 tabular-nums">
        {m.vendiendo}/{m.total - m.inactivas} vendiendo
      </div>
    </div>
  );
};

const Pendiente = ({ icono: Icono, texto, detalle, tono = 'normal', onClick }) => {
  const colores = {
    rojo: 'text-red-700 bg-red-50',
    ambar: 'text-amber-800 bg-amber-50',
    normal: 'text-gray-700 bg-gray-50'
  };
  return (
    <button onClick={onClick} className="w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-left hover:bg-gray-50 group">
      <span className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${colores[tono]}`}><Icono size={16} /></span>
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-medium text-gray-800">{texto}</span>
        {detalle && <span className="block text-xs text-gray-500 truncate">{detalle}</span>}
      </span>
      <ChevronRight size={16} className="text-gray-300 group-hover:text-gray-500" />
    </button>
  );
};

const ResumenDireccion = ({ onIr }) => {
  const { user } = useAuth();
  // El backend solo recalcula a pedido de un admin; para el resto el boton no haria nada.
  const esAdmin = user?.rol === 'admin';
  const [mes, setMes] = useState(periodoActual());
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');
  const [umbral] = useUmbralAlerta();

  const peticion = useRef(0);
  const cargar = useCallback(async (refrescar = false) => {
    const mia = ++peticion.current;
    setCargando(true);
    setError('');
    try {
      const res = await puntovService.resumenDireccion(mes, { alertaMin: umbral, refrescar });
      if (mia === peticion.current) setDatos(res.data);
    } catch (err) {
      if (mia === peticion.current) setError(err.message || 'No se pudo armar el resumen');
    } finally {
      if (mia === peticion.current) setCargando(false);
    }
  }, [mes, umbral]);

  useEffect(() => {
    setDatos(null);
    cargar();
    return () => { peticion.current++; };
  }, [cargar]);

  const v = datos?.ventas;
  const c = datos?.cobros;
  const p = datos?.pendientes;
  const tendencias = (v?.clientes || []).filter(x => x.variacion_pct !== null);
  const suben = [...tendencias].sort((a, b) => b.variacion_pct - a.variacion_pct).filter(x => x.variacion_pct > 0).slice(0, 3);
  const bajan = [...tendencias].sort((a, b) => a.variacion_pct - b.variacion_pct).filter(x => x.variacion_pct < 0).slice(0, 3);
  const activas = v ? v.maquinas.total - v.maquinas.inactivas : 0;
  const claseControl = 'h-9 px-2.5 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 outline-none';
  const tramoAnterior = datos ? `${fmtFecha(datos.rango_anterior.desde).slice(0, 5)}–${fmtFecha(datos.rango_anterior.hasta).slice(0, 5)}` : '';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="block text-xs font-medium text-gray-600 mb-1">Mes</span>
          <input type="month" value={mes} max={periodoActual()} onChange={(e) => e.target.value && setMes(e.target.value)} className={claseControl} />
        </label>
        {datos && (
          <p className="text-xs text-gray-500 pb-2">
            Ventas del {fmtFecha(datos.rango.desde)} al {fmtFecha(datos.rango.hasta)} comparadas con el {fmtFecha(datos.rango_anterior.desde)} al {fmtFecha(datos.rango_anterior.hasta)} · montos en USD
          </p>
        )}
        <div className="flex items-center gap-3 ml-auto">
          {datos && <span className="text-xs text-gray-500 tabular-nums">Datos de las {new Date(datos.generado_en).toLocaleTimeString('es-VE')}</span>}
          {esAdmin && (
            <button onClick={() => cargar(true)} disabled={cargando} className="h-9 px-3 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 flex items-center gap-2 text-sm disabled:opacity-60">
              <RefreshCw size={15} className={cargando ? 'animate-spin' : ''} /> Actualizar
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm flex items-start gap-2">
          <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" /> <span>{error}</span>
        </div>
      )}

      {cargando && !datos && (
        <div className="text-center py-16">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="mt-3 text-sm text-gray-500">Consultando todos los clientes…</p>
        </div>
      )}

      {datos && (
        <div className={`space-y-4 transition-opacity ${cargando ? 'opacity-60' : ''}`}>
          {/* Indicadores principales */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
            <Tarjeta titulo={`Venta de la red · ${fmtPeriodo(mes)}`} valor={v ? fmtUsd(v.totales.venta) : '—'}>
              {v && (
                <>
                  <div><Variacion pct={v.totales.variacion_pct} grande /> <span>vs {tramoAnterior} ({fmtUsd(v.totales.venta_anterior)})</span></div>
                  <div className="tabular-nums">{fmtEntero(v.totales.n_ventas)} ventas · {v.totales.clientes} clientes</div>
                </>
              )}
            </Tarjeta>
            <Tarjeta titulo="Ganancia bruta de la red" valor={v ? fmtUsd(v.totales.ganancia) : '—'}>
              {v && <div>Margen {fmtPct(v.totales.margen_pct)} · venta menos costo de producto</div>}
            </Tarjeta>
            <Tarjeta titulo="Por cobrar a clientes" valor={c ? fmtUsd(c.deuda_total_usd) : '—'} alerta={c?.en_mora.clientes > 0}>
              {c && (
                <>
                  <div>{c.clientes_con_deuda} de {c.clientes_con_cobro} clientes con deuda</div>
                  {c.en_mora.clientes > 0 && <div className="text-red-700 font-medium">{c.en_mora.clientes} en mora · {fmtUsd(c.en_mora.usd)}</div>}
                </>
              )}
            </Tarjeta>
            <Tarjeta titulo="Máquinas vendiendo ahora" valor={v ? `${v.maquinas.vendiendo} de ${activas}` : '—'}>
              {v && (
                <>
                  <div className="tabular-nums">
                    <span className="text-amber-700">{v.maquinas.sin_vender} sin vender</span> · <span>{v.maquinas.fuera_de_servicio} fuera de servicio</span>
                    {v.maquinas.inactivas > 0 && <> · {v.maquinas.inactivas} inactivas</>}
                  </div>
                  <div>Sin vender: más de {umbral >= 60 ? `${umbral / 60} h` : `${umbral} min`} sin una venta</div>
                </>
              )}
            </Tarjeta>
          </div>
          <ErrorSeccion mensaje={datos.errores.ventas} />
          <ErrorSeccion mensaje={datos.errores.cobros} />

          {/* Zonas propias vs clientes */}
          {v && (
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h3 className="text-sm font-semibold text-gray-800 mb-3">Venta por tipo de cuenta</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {[
                  { id: 'zona_propia', titulo: 'Zonas Punto V' },
                  { id: 'cliente', titulo: 'Clientes externos' },
                  { id: 'sin_clasificar', titulo: 'Sin clasificar' }
                ].map(t => {
                  const x = v.por_tipo[t.id];
                  return (
                    <div key={t.id} className="rounded-lg bg-gray-50 px-3 py-2.5">
                      <div className="text-xs text-gray-500">{t.titulo} · {x.clientes}</div>
                      <div className="text-lg font-semibold text-gray-900 tabular-nums">{fmtUsd(x.venta)}</div>
                      <div className="text-xs"><Variacion pct={x.variacion_pct} /> <span className="text-gray-500">· margen {fmtPct(x.margen_pct)}</span></div>
                    </div>
                  );
                })}
              </div>
              {v.por_tipo.sin_clasificar.clientes > 0 && (
                <p className="mt-2 text-xs text-gray-500">
                  {v.por_tipo.sin_clasificar.clientes} {v.por_tipo.sin_clasificar.clientes === 1 ? 'cuenta sin clasificar' : 'cuentas sin clasificar'}
                  {' '}({v.clientes.filter(x => !x.tipo).map(x => x.slug).join(', ')}). Se clasifican en la columna Tipo de
                  {' '}<button onClick={() => onIr('clientes')} className="text-blue-700 hover:underline">Clientes y máquinas</button>.
                  {datos.errores.clasificacion && ` (No se pudo leer la clasificación: ${datos.errores.clasificacion})`}
                </p>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 items-start">
            {/* Tabla de clientes y zonas */}
            <div className="xl:col-span-2 bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-800">Clientes y zonas</h3>
                <button onClick={() => onIr('monitor')} className="text-xs text-blue-700 hover:underline">Ver en el Monitor</button>
              </div>
              {v && v.clientes.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-xs text-gray-500">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium">Cliente</th>
                        <th className="px-4 py-2 text-right font-medium">Venta</th>
                        <th className="px-4 py-2 text-right font-medium">vs {tramoAnterior}</th>
                        <th className="px-4 py-2 text-right font-medium">Ganancia</th>
                        <th className="px-4 py-2 text-left font-medium">Máquinas</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {v.clientes.map(x => (
                        <tr key={x.base} className="hover:bg-gray-50">
                          <td className="px-4 py-2.5">
                            <div className="font-medium text-gray-900">
                              {x.slug}
                              {x.zona && <span className="ml-1.5 text-xs font-normal text-gray-500">{x.zona}</span>}
                            </div>
                            <div className="flex gap-1 mt-0.5">
                              {ETIQUETA_TIPO[x.tipo] && <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${ETIQUETA_TIPO[x.tipo].clase}`}>{ETIQUETA_TIPO[x.tipo].texto}</span>}
                              {x.suspendida && <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-700">Suspendida</span>}
                            </div>
                          </td>
                          <td className="px-4 py-2.5 text-right tabular-nums font-medium">{fmtUsd(x.venta)}</td>
                          <td className="px-4 py-2.5 text-right text-xs">
                            <Variacion pct={x.variacion_pct} />
                            <div className="text-gray-400 tabular-nums">{fmtUsd(x.venta_anterior)}</div>
                          </td>
                          <td className="px-4 py-2.5 text-right tabular-nums">
                            {fmtUsd(x.ganancia)}
                            <div className="text-xs text-gray-400">{fmtPct(x.margen_pct)}</div>
                          </td>
                          <td className="px-4 py-2.5"><BarraMaquinas m={x.maquinas} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="px-4 py-8 text-center text-sm text-gray-500">Sin clientes con base para mostrar.</p>
              )}
              {v?.errores.length > 0 && (
                <p className="px-4 py-2 text-xs text-amber-700 border-t border-gray-100">
                  No se pudieron consultar: {v.errores.map(e => e.slug).join(', ')}. Sus ventas no están en los totales.
                </p>
              )}
            </div>

            <div className="space-y-4">
              {/* Tendencias */}
              {v && (suben.length > 0 || bajan.length > 0) && (
                <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
                  <h3 className="text-sm font-semibold text-gray-800">Tendencia vs mes anterior</h3>
                  {bajan.length > 0 && (
                    <div>
                      <div className="text-xs font-medium text-red-700 mb-1">Bajan</div>
                      {bajan.map(x => (
                        <div key={x.base} className="flex justify-between text-sm py-0.5"><span className="truncate">{x.slug}</span><Variacion pct={x.variacion_pct} /></div>
                      ))}
                    </div>
                  )}
                  {suben.length > 0 && (
                    <div>
                      <div className="text-xs font-medium text-green-700 mb-1">Suben</div>
                      {suben.map(x => (
                        <div key={x.base} className="flex justify-between text-sm py-0.5"><span className="truncate">{x.slug}</span><Variacion pct={x.variacion_pct} /></div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Cuentas por cobrar */}
              {c && (
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-gray-800">Cuentas por cobrar</h3>
                    <button onClick={() => onIr('pagos')} className="text-xs text-blue-700 hover:underline">Estado de pagos</button>
                  </div>
                  {c.deuda_total_usd > 0 ? (
                    <>
                      <div className="space-y-1.5">
                        {c.antiguedad.map(t => (
                          <div key={t.id} className="text-xs">
                            <div className="flex justify-between"><span className="text-gray-600">{t.etiqueta}</span><span className="tabular-nums">{fmtUsd(t.usd)}</span></div>
                            <div className="h-1.5 bg-gray-100 rounded">
                              {t.usd > 0 && <div className={`h-1.5 rounded ${t.id === 'por_vencer' ? 'bg-blue-400' : t.id === '1_30' ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${(t.usd / c.deuda_total_usd) * 100}%` }} />}
                            </div>
                          </div>
                        ))}
                      </div>
                      <ul className="mt-3 pt-3 border-t border-gray-100 space-y-1.5">
                        {c.deudores.slice(0, 5).map(d => (
                          <li key={d.base} className="flex items-center justify-between gap-2 text-xs">
                            <span className="min-w-0">
                              <span className="font-medium text-gray-800">{d.slug}</span>
                              <span className="text-gray-400"> · {d.periodo ? fmtPeriodo(d.periodo) : ''}{d.dias_vencidos > 0 ? ` · ${d.dias_vencidos} d vencido` : ''}</span>
                              {d.recarga_bloqueada && <span className="ml-1 px-1 rounded bg-red-100 text-red-700">recarga cortada</span>}
                            </span>
                            <span className="tabular-nums font-medium">{fmtUsd(d.deuda_usd)}</span>
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : (
                    <p className="text-sm text-green-700">Ningún cliente con deuda.</p>
                  )}
                  {c.hoy && <p className="mt-2 text-[11px] text-gray-400">Estado de cobro de Punto V al {fmtFecha(c.hoy)}.</p>}
                </div>
              )}

              {/* Pendientes */}
              <div className="bg-white rounded-xl border border-gray-200 p-2">
                <h3 className="text-sm font-semibold text-gray-800 px-2 pt-2 pb-1">Pendientes</h3>
                {v && v.maquinas.sin_vender > 0 && (
                  <Pendiente icono={Activity} tono="ambar" texto={`${v.maquinas.sin_vender} máquinas sin vender`} detalle="Revisar en el Monitor" onClick={() => onIr('monitor')} />
                )}
                {p.facturas && p.facturas.abiertas > 0 && (
                  <Pendiente
                    icono={Receipt}
                    tono={p.facturas.vencidas > 0 ? 'rojo' : p.facturas.por_vencer > 0 ? 'ambar' : 'normal'}
                    texto={`${p.facturas.abiertas} solicitudes de factura por enviar`}
                    detalle={[p.facturas.vencidas && `${p.facturas.vencidas} vencidas`, p.facturas.por_vencer && `${p.facturas.por_vencer} vencen en 2 días o menos`].filter(Boolean).join(' · ') || null}
                    onClick={() => onIr('facturas')}
                  />
                )}
                {c && c.fallos_cobro.length > 0 && (
                  <Pendiente icono={Wallet} tono="rojo" texto={`${c.fallos_cobro.length} cobros con fallo`} detalle={c.fallos_cobro.map(f => f.slug).join(', ')} onClick={() => onIr('pagos')} />
                )}
                {p.suspendidos && p.suspendidos.length > 0 && (
                  <Pendiente icono={Ban} tono="rojo" texto={`${p.suspendidos.length} clientes suspendidos`} detalle={p.suspendidos.map(s => s.slug).join(', ')} onClick={() => onIr('clientes')} />
                )}
                {datos.cortesias && datos.cortesias.length > 0 && (
                  <Pendiente
                    icono={Gift}
                    texto={`${datos.cortesias.length} cortesías en ${fmtPeriodo(mes)}`}
                    detalle={datos.cortesias.map(x => x.slug).join(', ')}
                    onClick={() => onIr('cobro')}
                  />
                )}
                {p.bases && p.bases.con_problema > 0 && (
                  <Pendiente icono={Database} tono="ambar" texto={`${p.bases.con_problema} bases con problemas`} detalle="Bases inexistentes o con tablas faltantes" onClick={() => onIr('bases')} />
                )}
                {!(v?.maquinas.sin_vender) && !(p.facturas?.abiertas) && !(c?.fallos_cobro.length) && !(p.suspendidos?.length)
                  && !(datos.cortesias?.length) && !(p.bases?.con_problema) && (
                  <p className="px-2 pb-3 text-sm text-green-700">Nada pendiente.</p>
                )}
                <div className="px-2"><ErrorSeccion mensaje={datos.errores.facturas || datos.errores.bases || datos.errores.cortesias} /></div>
              </div>
            </div>
          </div>

          <p className="text-xs text-gray-400">
            Ventas: Punto V, solo ventas aprobadas, de los clientes con base y sin archivar; la ganancia es bruta (venta menos costo
            del producto, sin gastos de operación). Cobros: estado actual en Punto V, no depende del mes elegido. Los datos se
            guardan 5 minutos; "Actualizar" los vuelve a consultar.
          </p>
        </div>
      )}
    </div>
  );
};

export default ResumenDireccion;
